import React, { useEffect, useState } from "react";
import { CloudUpload } from "lucide-react";
import { TspFormField, TspModal } from "../../../components/primitives";
import { tspErrorMessage } from "../../../hooks/useTspApiResource";
import JourneyVisitFields from "./JourneyVisitFields";
import { ACCEPTED_FILE_TYPES, MEDIA_CAPTION_MAX, formatFileSize, mediaTypeOfFile, validateMediaUpload } from "../mediaModel";

// Upload dialog. The file is only inspected in the browser for a quick type/size
// check and a preview; the server validates its real content. Media can only be
// added to an IN_PROGRESS journey's recorded city visit. `context` says how much
// is already decided:
//   - { journeyId, cityVisitId, label }: opened from a specific city — no picker
//     and no request for journeys or visits at all;
//   - { journeyId }: only that journey's visits are requested;
//   - none: the in-progress journeys, then the chosen journey's visits.
// Mounted only while open, so its state resets each time. `onSubmit` rejects
// with the server's refusal, which is shown here and keeps the dialog open.
export default function MediaUploadModal({ context, onClose, onSubmit }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selection, setSelection] = useState({ journeyId: "", visitId: "" });

  const fullyFixed = Boolean(context?.journeyId && context?.cityVisitId);
  const journeyId = context?.journeyId ? String(context.journeyId) : selection.journeyId;
  const cityVisitId = fullyFixed ? String(context.cityVisitId) : selection.visitId;

  // Local preview for images; always revoked so object URLs don't leak.
  useEffect(() => {
    if (!file || mediaTypeOfFile(file) !== "IMAGE") {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const values = { caption, journeyId, cityVisitId };
    const found = validateMediaUpload(values, file);
    setErrors(found);
    setServerError("");
    const firstInvalid = ["file", "caption", "journeyId", "cityVisitId"].find((key) => found[key]);
    if (firstInvalid) {
      document.getElementById(`tsp-media-${firstInvalid}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values, file);
    } catch (error) {
      setServerError(tspErrorMessage(error, "Could not upload this file. Please try again."));
      setSubmitting(false);
    }
  };

  const type = mediaTypeOfFile(file);

  return (
    <TspModal
      open
      title="Upload Media"
      onClose={submitting ? undefined : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="tsp-media-upload-form" className="tsp-btn tsp-btn-success" disabled={submitting}>
            {submitting ? "Uploading…" : "Upload"}
          </button>
        </>
      }
    >
      <form id="tsp-media-upload-form" onSubmit={handleSubmit} noValidate className="tsp-media-upload-form" aria-busy={submitting}>
        {serverError && (
          <p className="tsp-form-error" role="alert">
            {serverError}
          </p>
        )}

        <TspFormField id="tsp-media-file" label="Photo or video" required error={errors.file} hint="Images up to 10 MB, videos up to 200 MB.">
          <input
            id="tsp-media-file"
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="tsp-file-input"
            disabled={submitting}
            aria-invalid={errors.file ? "true" : undefined}
            aria-describedby={errors.file ? "tsp-media-file-error" : "tsp-media-file-hint"}
            onChange={(event) => {
              setFile(event.target.files?.[0] || null);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
          />
        </TspFormField>

        {file && (
          <div className="tsp-upload-preview">
            {previewUrl ? (
              <img src={previewUrl} alt="Selected file preview" />
            ) : (
              <span className="tsp-upload-preview-icon" aria-hidden="true">
                <CloudUpload size={22} />
              </span>
            )}
            <span className="tsp-upload-preview-text">
              <strong>{file.name}</strong>
              <span>
                {type === "VIDEO" ? "Video" : type === "IMAGE" ? "Photo" : "Unsupported file"} · {formatFileSize(file.size)}
              </span>
            </span>
          </div>
        )}

        <TspFormField id="tsp-media-caption" label="Caption" required error={errors.caption} hint={`${caption.length}/${MEDIA_CAPTION_MAX}`}>
          <input
            id="tsp-media-caption"
            type="text"
            className="tsp-field-control"
            value={caption}
            maxLength={MEDIA_CAPTION_MAX + 20}
            autoComplete="off"
            disabled={submitting}
            aria-invalid={errors.caption ? "true" : undefined}
            aria-describedby={errors.caption ? "tsp-media-caption-error" : "tsp-media-caption-hint"}
            onChange={(event) => {
              setCaption(event.target.value);
              setErrors((prev) => ({ ...prev, caption: undefined }));
            }}
          />
        </TspFormField>

        {context?.label && (
          <p className="tsp-muted-text">
            For <strong>{context.label}</strong>
          </p>
        )}
        {!fullyFixed && <JourneyVisitFields fixedJourneyId={context?.journeyId} onSelect={setSelection} errors={errors} disabled={submitting} />}
      </form>
    </TspModal>
  );
}
